import type { BylinePageAdf } from '../hooks/useBylinePageAdf.ts';

import type {
	AtAGlanceBrowserObservation,
	AtAGlanceObservationSnapshot,
} from './atAGlanceObservation.ts';
import { createAtAGlancePanelWait } from './atAGlancePanelWait.ts';
import type { AtAGlanceAcceptedResult } from './atAGlanceResultCache.ts';
import type { AtAGlanceCapturedOverviewCommit } from './atAGlanceReuseObservation.ts';
import type { AtAGlanceStatus } from './atAGlanceTypes.ts';

export type AtAGlanceObservedResult = {
	acceptedResult?: AtAGlanceAcceptedResult;
	hasOverview: boolean;
	status?: AtAGlanceStatus;
	failed: boolean;
	loading: boolean;
};

export type AtAGlancePanelFields = {
	panelOpeningIndex?: number;
	panelStartKnown?: boolean;
	panelOpenedAtMs?: number;
	panelOutcome?: 'overviewCommitted' | 'noOverview' | 'departedPending' | 'departedUnknown';
	overviewReady?: boolean;
	outputStatus?: AtAGlanceStatus;
	requestFailed?: boolean;
	documentOutcome?: BylinePageAdf['documentOutcome'];
	documentReady?: boolean;
	panelWaitMs?: number;
	inputWaitMs?: number;
	documentWaitMs?: number;
	retryWaitMs?: number;
	responseWaitMs?: number;
	unknownWaitMs?: number;
	waitTimingCoverage?: 'complete' | 'partial';
};

export type AtAGlancePanelObservation = {
	observePanel: (open: boolean) => void;
	observeDocument: (outcome: BylinePageAdf['documentOutcome'], ready: boolean | undefined) => void;
	observeResult: (result: AtAGlanceObservedResult) => void;
	observeOverviewCommit: (commit: AtAGlanceCapturedOverviewCommit) => void;
	refresh: () => void;
	close: () => void;
};

const getPanelStateKey = (
	fields: AtAGlancePanelFields,
	snapshot: AtAGlanceObservationSnapshot,
): string =>
	JSON.stringify({
		...fields,
		...snapshot,
		// Elapsed time alone is not a new panel state; each report still carries current totals.
		inputWaitMs: undefined,
		documentWaitMs: undefined,
		retryWaitMs: undefined,
		responseWaitMs: undefined,
		unknownWaitMs: undefined,
		resultAgeMs: undefined,
	});

const getDepartureOutcome = (
	snapshot: AtAGlanceObservationSnapshot,
): 'departedPending' | 'departedUnknown' => {
	if (snapshot.decision === 'waitingForInput' || snapshot.decision === 'pendingMatch') {
		return 'departedPending';
	}
	if (snapshot.phase === 'activeAttempt' || snapshot.phase === 'retryDelay') {
		return 'departedPending';
	}
	return 'departedUnknown';
};

// CCINT-9573: transport success cannot tell whether the active panel committed a useful overview.
export const createAtAGlancePanelObservation = ({
	now,
	report,
	getSnapshot,
}: {
	now: () => number;
	report: (event: Omit<AtAGlanceBrowserObservation, 'visitId' | 'browserTimeMs'>) => void;
	getSnapshot: () => AtAGlanceObservationSnapshot;
}): AtAGlancePanelObservation => {
	let observedOpen: boolean | undefined;
	let openingIndex = 0;
	let startKnown = false;
	let committed = false;
	let pendingCommit: AtAGlanceCapturedOverviewCommit | undefined;
	let outcome: AtAGlancePanelFields['panelOutcome'];
	let result: AtAGlanceObservedResult | undefined;
	let documentOutcome: BylinePageAdf['documentOutcome'] = 'unknown';
	let documentReady: boolean | undefined;
	let lastState: string | undefined;
	const wait = createAtAGlancePanelWait(now);
	const accountWait = () => wait.account(observedOpen === true, documentReady);

	const fields = (): AtAGlancePanelFields => {
		const openedAt = wait.getOpenedAt();
		return {
			panelOpeningIndex: openingIndex,
			panelStartKnown: startKnown,
			...(startKnown && openedAt !== undefined ? { panelOpenedAtMs: openedAt } : {}),
			panelOutcome: outcome,
			overviewReady: result?.hasOverview,
			outputStatus: result?.status,
			requestFailed: result?.failed,
			documentOutcome,
			documentReady,
			...wait.fields(startKnown),
		};
	};
	const reconcileCommit = () => {
		if (!observedOpen || !pendingCommit) {
			return;
		}
		if (pendingCommit.acceptedResult && result?.acceptedResult !== pendingCommit.acceptedResult) {
			return;
		}
		pendingCommit.qualify();
		pendingCommit = undefined;
	};
	const refresh = () => {
		accountWait();
		const snapshot = getSnapshot();
		wait.observeRequest(snapshot);
		if (!observedOpen) {
			return;
		}
		// Only a resolved response or an observed failure establishes no overview. Initial bridge data does not.
		if (
			!committed &&
			result &&
			!result.hasOverview &&
			!result.loading &&
			(result.status || result.failed)
		) {
			outcome = 'noOverview';
		}
		const state = getPanelStateKey(fields(), snapshot);
		if (state !== lastState) {
			lastState = state;
			report({
				stage: 'panelState',
				phase: snapshot.phase,
				decision: snapshot.decision,
				...fields(),
			});
		}
	};
	const close = () => {
		pendingCommit = undefined;
		if (!observedOpen) {
			return;
		}
		accountWait();
		const snapshot = getSnapshot();
		const departureOutcome = getDepartureOutcome(snapshot);
		report({
			stage: 'panelFinished',
			phase: snapshot.phase,
			decision: snapshot.decision,
			...fields(),
			panelOutcome: outcome ?? departureOutcome,
		});
		observedOpen = false;
	};
	const observePanel = (open: boolean) => {
		if (!open) {
			close();
			observedOpen = false;
			return;
		}
		if (open === observedOpen) {
			return;
		}
		startKnown = observedOpen === false;
		observedOpen = true;
		openingIndex += 1;
		wait.start();
		committed = false;
		outcome = undefined;
		lastState = undefined;
		report({
			stage: 'panelOpened',
			phase: getSnapshot().phase,
			decision: getSnapshot().decision,
			...fields(),
		});
		// Child effects can report the overview before the parent's opening effect runs.
		if (pendingCommit) {
			committed = true;
			wait.overviewCommitted();
			outcome = 'overviewCommitted';
			reconcileCommit();
		}
		refresh();
	};
	const observeDocument = (next: BylinePageAdf['documentOutcome'], ready: boolean | undefined) => {
		accountWait();
		documentOutcome = next ?? 'unknown';
		documentReady = ready;
		refresh();
	};
	const observeResult = (next: AtAGlanceObservedResult) => {
		result = next;
		reconcileCommit();
		refresh();
	};
	const observeOverviewCommit = (commit: AtAGlanceCapturedOverviewCommit) => {
		pendingCommit = commit;
		if (!observedOpen) {
			return;
		}
		reconcileCommit();
		if (committed) {
			refresh();
			return;
		}
		accountWait();
		committed = true;
		wait.overviewCommitted();
		outcome = 'overviewCommitted';
		refresh();
	};
	return {
		observePanel,
		observeDocument,
		observeResult,
		observeOverviewCommit,
		refresh,
		close,
	};
};
